import { useMemo } from "react";
import { toNestedStructure } from "../models/utils";
import useResources from "./use-resources";
import { ProfileStatus } from "../models/profile";

export function useTenantProfiles(tenantId) {
  const {
    resources: profiles,
    isLoading,
    error,
    mutate,
    update,
    create,
    del,
    axios,
  } = useResources(tenantId ? `tenants/${tenantId}/profiles` : null);

  const structProfiles = useMemo(
    () =>
      toNestedStructure(
        profiles,
        ["garantor_of", "legal_representative_of"],
        ["garantors", "legal_representatives"],
      ),
    [profiles],
  );

  function updateProfile(profileId, profile) {
    return update(profileId, profile, { alternateRootPath: `profiles-indiv` });
  }

  function createProfile(profile) {
    return create(
      { ...profile, id_tenant: tenantId },
      { alternateRootPath: `profiles-indiv` },
    );
  }

  function archiveProfile(profileId) {
    return updateProfile(profileId, { status: ProfileStatus.Archive.status });
  }

  function activateProfile(profileId) {
    return updateProfile(profileId, { status: ProfileStatus.Actif.status });
  }

  function deleteProfile(profileId) {
    return del(profileId, { alternateRootPath: `profiles-indiv` });
  }

  async function inviteProfiles(profileIds) {
    await axios.post(`tenants/${tenantId}/invite`, {
      profile_ids: profileIds,
    });
    await mutate();
  }

  async function revokeInviteProfiles(profileIds) {
    await axios.post(`tenants/${tenantId}/revoke-invite`, {
      profile_ids: profileIds,
    });
    await mutate();
  }

  return {
    profiles,
    structProfiles,
    updateProfile,
    archiveProfile,
    activateProfile,
    createProfile,
    deleteProfile,
    inviteProfiles,
    revokeInviteProfiles,
    isLoading,
    error,
    refresh: () => mutate(),
  };
}

export function useCandidatProfiles(options = {}) {
  const { withById = false } = options;
  const {
    resources: profiles,
    resourcesById: profilesById,
    isLoading,
    error,
    create,
    update,
    del,
  } = useResources(`profiles/candidats`, {}, { withById });

  const structProfiles = useMemo(
    () => toNestedStructure(profiles, "garantor_of", "garantors"),
    [profiles],
  );

  function createProfile(profile) {
    return create(
      { ...profile, is_candidat: true },
      { alternateRootPath: `profiles-indiv` },
    );
  }

  function updateProfile(profileId, profile) {
    return update(profileId, profile, { alternateRootPath: `profiles-indiv` });
  }

  function deleteProfile(profileId) {
    return del(profileId, { alternateRootPath: `profiles-indiv` });
  }

  async function removeGarantor(garantId) {
    try {
      await updateProfile(garantId, { garantor_of: null });
    } catch (error) {
      console.error(error);
    }
  }

  return {
    profiles,
    profilesById,
    structProfiles,
    removeGarantor,
    createProfile,
    updateProfile,
    deleteProfile,
    isLoading,
    error,
  };
}
